import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import Header from '../components/Header/Header';
import SEO from '../components/SEO/SEO';
import MenuSection from '../components/MenuSection/MenuSection';
import { useTimeTracker } from '../hooks/useTimeTracker';
import { trackMenuTabChange, trackReservationButtonClick, trackPhoneClick } from '../utils/analytics';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import './MenuPage.css';

const MenuPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('pizza');
  const [ramadanMenu, setRamadanMenu] = useState(null);
  const [showRamadanMenu, setShowRamadanMenu] = useState(false);
  
  // Tracker le temps passé sur la page menu
  useTimeTracker('menu_page', true);

  useEffect(() => {
    const loadRamadanMenu = async () => {
      try {
        const docRef = doc(db, 'siteSettings', 'ramadanMenu');
        const snap = await getDoc(docRef);
        if (snap.exists()) {
          const data = snap.data();
          setRamadanMenu(data);
          if (data.imageUrl && data.enabled !== false) {
            setShowRamadanMenu(true);
          }
        }
      } catch (error) {
        console.error('Erreur lors du chargement du menu Ramadan:', error);
      }
    };

    loadRamadanMenu();
  }, []);

  const formatDisplayDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: 'long'
    });
  };

  return (
    <div className="menu-page">
      <SEO 
        title="Menu" 
        description="Explorez la carte d'Aqua Farina : Large choix de pizzas gourmandes, pâtes italiennes et desserts faits maison. Qualité et fraîcheur au cœur des Jardins de Carthage."
        canonical="/menu"
        keywords="menu pizza, carte restaurant, spécialités italiennes, prix pizza"
      />
      <Header />
      <div className="menu-header">
        <button className="back-button" onClick={() => navigate('/')}>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="back-icon"
          >
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
        </button>
        <h1 className="menu-title">{t('aquaFarina')}</h1>
      </div>

      {showRamadanMenu && ramadanMenu?.imageUrl ? (
        <div className="ramadan-menu-container">
          {(ramadanMenu?.menuDate || ramadanMenu?.dateFrom || ramadanMenu?.dateTo) && (
            <div className="ramadan-menu-date">
              {ramadanMenu.menuDate
                ? `Menu du ${formatDisplayDate(ramadanMenu.menuDate)}`
                : ramadanMenu.dateFrom && ramadanMenu.dateTo
                  ? `Menu du ${formatDisplayDate(ramadanMenu.dateFrom)} au ${formatDisplayDate(ramadanMenu.dateTo)}`
                  : ramadanMenu.dateFrom
                    ? `Menu du ${formatDisplayDate(ramadanMenu.dateFrom)}`
                    : `Menu du ${formatDisplayDate(ramadanMenu.dateTo)}`}
            </div>
          )}
          <img
            src={ramadanMenu.imageUrl}
            alt="Menu Ramadan"
            className="ramadan-menu-image"
          />
          <div className="ramadan-menu-actions">
            <button
              className="btn-ramadan-secondary"
              onClick={() => {
                setShowRamadanMenu(false);
                setActiveTab('pizza');
              }}
            >
              Menu hors ramadan
            </button>
            <button
              className="btn-reserve"
              onClick={() => {
                trackReservationButtonClick();
                navigate('/reservation');
              }}
            >
              {t('reserve')}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="menu-actions">
            <button className="btn-reserve" onClick={() => { trackReservationButtonClick(); navigate('/reservation'); }}>
              {t('reserve')}
            </button>
            <a href="tel:+21626773104" className="btn-call" onClick={trackPhoneClick}>
              {t('call')}
            </a>
          </div>
          <div className="menu-tabs">
            <button
              className={`tab-button ${activeTab === 'pizza' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('pizza');
                trackMenuTabChange('pizza');
              }}
            >
              {t('pizza')}
            </button>
            <button
              className={`tab-button ${activeTab === 'main' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('main');
                trackMenuTabChange('main');
              }}
            >
              {t('mainMenu')}
            </button>
          </div>
        </>
      )}

      {!showRamadanMenu && (
        <>
          <div className="menu-content">
            {activeTab === 'pizza' && (
              <MenuSection menuType="pizza" />
            )}
            {activeTab === 'main' && (
              <MenuSection menuType="main" />
            )}
          </div>

          <div className="menu-tabs menu-tabs-bottom">
            <button
              className={`tab-button ${activeTab === 'pizza' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('pizza');
                trackMenuTabChange('pizza');
              }}
            >
              {t('pizza')}
            </button>
            <button
              className={`tab-button ${activeTab === 'main' ? 'active' : ''}`}
              onClick={() => {
                setActiveTab('main');
                trackMenuTabChange('main');
              }}
            >
              {t('mainMenu')}
            </button>
          </div>
          <div className="menu-actions menu-actions-bottom">
            <button className="btn-reserve" onClick={() => { trackReservationButtonClick(); navigate('/reservation'); }}>
              {t('reserve')}
            </button>
            <a href="tel:+21626773104" className="btn-call" onClick={trackPhoneClick}>
              {t('call')}
            </a>
          </div>
        </>
      )}
    </div>
  );
};

export default MenuPage;


